import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ImagePicker from "components/image-picker/image-picker.component";
import FormikInput from "components/input/formik-input";
import { displayError, displaySuccess } from "components/Toast/toast.component";
import { Form, Formik } from "formik";
import { FileType } from "interfaces/MediaPickerInterface";
import { IEndUserData } from "interfaces/user.interface";
import React, { useEffect, useState } from "react";
import { getEndUserData, updateUserData } from "services/setting.service";
import * as Yup from "yup";
import Button from "components/button/button";

const EndUserData: React.FC = () => {
  const [userData, setUserData] = useState<IEndUserData>();
  const queryClient = useQueryClient()
  const { data: endUserInfo, refetch } = useQuery({
    queryKey: ["getEndUserData"],
    queryFn: getEndUserData,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  useEffect(() => {
    if (endUserInfo) {
      setUserData(endUserInfo);
    }
  }, [endUserInfo]);

  const userValidationSchema = Yup.object().shape({
    profile: Yup.object().shape({
      first_name: Yup.string().required("First name is required."),
      last_name: Yup.string().required("Last name is required."),
    }),
  });

  const handleUserInfo = useMutation({
    mutationFn: (data: FormData) => updateUserData(data),
    retry: 0,
    onSuccess: () => {
      refetch();
      displaySuccess("User detail changed successfully.");
      queryClient.invalidateQueries({ queryKey: ['getEndUserData'] })
    },
    onError: () => {
      displayError("Could not update user details.");
    },
  });

  const handleUserInfoSubmit = (values: IEndUserData) => {
    const formData = new FormData();
    if (values.avatar) {
      formData.append("avatar", values.avatar);
    }
    formData.append("first_name", values.profile.first_name);
    formData.append("last_name", values.profile.last_name);

    handleUserInfo.mutateAsync(formData);
  };

  return (
    <div className="py-4 ">
      {userData && (
        <Formik
          initialValues={userData}
          validationSchema={userValidationSchema}
          onSubmit={handleUserInfoSubmit}
        >
          {({ setFieldValue, errors, values }) => (
            <Form>
              <ImagePicker
                id="icon"
                name="icon"
                media={values.profile.avatar}
                className="w-[62px] h-[62px] relative"
                svgPenClassName={`relative ${values.profile.avatar ? "-bottom-[10px]" : "bottom-2"
                  } right-[0px]`}
                svgWidth={27}
                svgHeight={27}
                order={1}
                enableAutoUpload={false}
                onFileUploaded={(e: FileType) => {
                  setFieldValue("avatar", e);
                }}
              />
              <div className="flex flex-col gap-4 pt-2">
                <div className="flex gap-4 text-start">
                  <div className="flex-1">
                    <FormikInput
                      id="first_name"
                      name="profile.first_name"
                      placeholder="First Name"
                      as="input"
                      showLabel={true}
                      label="First Name"
                      onDataChanged={() => { }}
                      maxLength={50}
                    />
                  </div>
                  <div className="flex-1">
                    <FormikInput
                      id="last_name"
                      name="profile.last_name"
                      placeholder="Last Name"
                      as="input"
                      showLabel={true}
                      label="Last Name"
                      onDataChanged={() => { }}
                      maxLength={50}
                    />
                  </div>
                </div>
                <div className="w-full text-start">
                  <FormikInput
                    disabled
                    id="email"
                    name="email"
                    placeholder="Email"
                    as="input"
                    showLabel={true}
                    label="Email"
                    maxLength={50}
                  />
                </div>
              </div>
              <Button
                variant="primary"
                type="submit"
                className="mt-6"
              >
                Update Info
              </Button>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default EndUserData;
